import { useEffect, useState } from "react";
import { getImage } from "./serviceAPI/image";

/**
 * useImage Hook
 * @param path - Optional. Image path in the service.
 * @param fetch - Optional. Image object with the base64 string and the type of the image.
 *
 * If you already have fetched the image and want to parse the image source correctly,
 * just pass in the image with the respective type in the `fetch` attribute.
 *
 * @returns source string for image or null if no image is available.
 */
export const useImage = (
  path: string | undefined,
  fetch: { base64: string; type: string } | undefined,
): string | null => {
  const [img, setImg] = useState<{ image: string; type: string } | null>(
    fetch ? { image: fetch.base64, type: fetch.type } : null,
  );

  useEffect(() => {
    if (!fetch) {
      const fetchImg = async () => {
        if (path) {
          try {
            const result = await getImage(path);
            setImg({ image: result.base64, type: result.type });
          } catch (error) {
            console.error("Error fetching image:", error);
            setImg(null); // Set to null on error
          }
        } else {
          setImg(null); // Clear image if path is undefined
        }
      };
      fetchImg();
    } else {
      setImg({ image: fetch.base64, type: fetch.type });
    }
  }, [path, fetch]);

  if (img) {
    return `data:${img.type};base64,${img.image}`;
  } else {
    return null;
  }
};
